import React, { useRef, useEffect } from 'react';
import { useLocation, Switch } from 'react-router-dom';
import AppRoute from './utils/AppRoute';
import ScrollReveal from './utils/ScrollReveal';
import ReactGA from 'react-ga';
import routes from './seo/routes';

// Layouts
import LayoutDefault from './layouts/LayoutDefault';

// Views
import Home from './views/Home';
import Privacy from './views/Privacy';
import ContentPage from './views/ContentPage';
import GlossaryPage from './views/GlossaryPage';
import CaseStudyPage from './views/CaseStudyPage';

const { ROUTES, localizedPath, LANGS } = routes;

// Google Analytics is optional: the code is injected at build time and is not
// available during prerendering, so never let a missing id throw.
const GA_CODE = process.env.REACT_APP_GA_CODE;
const isPrerender = typeof window !== 'undefined' && window.__PRERENDER__ === true;
const analyticsEnabled = Boolean(GA_CODE) && !isPrerender;

if (analyticsEnabled) {
  ReactGA.initialize(GA_CODE);
}

const trackPage = page => {
  if (!analyticsEnabled) return;
  ReactGA.set({ page });
  ReactGA.pageview(page);
};

/**
 * Route key -> view component. Every key in `ROUTES` must appear here.
 * The four long-form pages share one view, which reads its copy from
 * src/seo/content.js by the same key.
 */
const contentPage = (routeKey) => () => <ContentPage routeKey={routeKey} />;

/** The two production cases share one view; the case key selects the copy. */
const caseStudy = (routeKey, caseKey) => () => (
  <CaseStudyPage routeKey={routeKey} caseKey={caseKey} />
);

const VIEWS = {
  home: Home,
  moodleAgent: contentPage('moodleAgent'),
  whatsappAgent: contentPage('whatsappAgent'),
  agenticPipelines: contentPage('agenticPipelines'),
  about: contentPage('about'),
  caseEstudios: caseStudy('caseEstudios', 'estudiosTributarios'),
  caseProsperia: caseStudy('caseProsperia', 'prosperia'),
  glossary: GlossaryPage,
  privacy: Privacy,
};

const App = () => {

  const childRef = useRef();
  let location = useLocation();

  useEffect(() => {
    const page = location.pathname;
    document.body.classList.add('is-loaded')
    childRef.current.init();
    trackPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <ScrollReveal
      ref={childRef}
      children={() => (
        <Switch>
          {/* One <Route> per (route, language) pair, generated from the shared
              route list so the router, the sitemap and the prerenderer agree. */}
          {ROUTES.map(route =>
            LANGS.map(lang => (
              <AppRoute
                key={`${lang}:${route.key}`}
                exact
                path={localizedPath(route.key, lang)}
                component={VIEWS[route.key]}
                layout={LayoutDefault} />
            ))
          )}
        </Switch>
      )} />
  );
}

export default App;
